import {
	Alert,
	Box,
	Button,
	Dialog,
	Grid,
	Input,
	Step,
	StepLabel,
	Stepper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Clients } from "../graphql/querys/clients";
import { useMutation, useQuery } from "@apollo/client";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CreateRenting } from "../graphql/mutation/createRenting";
import { getErrorMessage } from "../helpers";
import { useTranslation } from "react-i18next";

const ChooseClient = (props) => {
	const { t } = useTranslation();
	const { setChosenClient, setActiveStep } = props;
	const [term, setTerm] = useState("");
	const [limit, setLimit] = useState(9);
	const [offSet, setOffSet] = useState(0);
	const { loading, error, data, refetch } = useQuery(Clients, {
		variables: { term, limit, offSet },
	});

	return (
		<Grid container width="100%" direction="column" spacing={2} padding={3}>
			<Grid item>
				<Typography variant="h5" textAlign="center" fontWeight="bold">
					{t("RENTING.CHOOSE_CLIENT")}
				</Typography>
			</Grid>
			<Grid item>
				<TextField
					fullWidth
					variant="outlined"
					placeholder={t("RENTING.SEARCH_CLIENT")}
					value={term}
					onChange={(e) => setTerm(e.target.value)}
				/>
			</Grid>
			<Grid item>
				<Box
					sx={{
						maxHeight: "30vh",
						overflowY: "auto",
						border: "1px solid #ddd",
						borderRadius: "8px",
					}}
				>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>{t("FIELDS.name")}</TableCell>
								<TableCell>{t("FIELDS.contact")}</TableCell>
								<TableCell></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data?.clients?.data.map((client) => (
								<TableRow key={client._id}>
									<TableCell>{client.name}</TableCell>
									<TableCell>{client.phoneNumber}</TableCell>
									<TableCell>
										<Button
											variant="contained"
											color="primary"
											sx={{
												backgroundColor: "#63D77E",
												color: "white",
												"&:hover": { backgroundColor: "#4CAF50" },
											}}
											onClick={() => {
												setChosenClient(client._id);
												setActiveStep(1);
											}}
										>
											{t("ACTIONS.CHOOSE")}
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Grid>
		</Grid>
	);
};

const MoreDetails = (props) => {
	const { t } = useTranslation();
	const { setData, dataHere, submit } = props;
	// Initialize the dates to today by default
	const [valueDate, setValueDate] = useState(dayjs().startOf('day'));
	const [valueStartDate, setValueStartDate] = useState(dayjs().startOf('day'));

	const handleChangeEndDate = (newValue) => {
		setValueDate(newValue || dayjs().startOf('day')); // Fallback to today's date
	};

	const handleChangeStartDate = (newValue) => {
		setValueStartDate(newValue || dayjs().startOf('day')); // Fallback to today's date
	};

	const [valuePrice, setValuePrice] = useState(0);
	const [valueResp, setValueResp] = useState("");
	const [valuePayment, setValuePayment] = useState(0);

	useEffect(() => {
		let diffInDays = valueDate.diff(valueStartDate, "day");
		setValuePayment((diffInDays + 1) * valuePrice);
	}, [valueDate, valueStartDate, valuePrice]);

	const saveDetails = () => {
		const newData = {
			pricePerDay: valuePrice,
			startedRent: valueStartDate,
			previsionEndingRent: valueDate,
			responsible: valueResp,
		};
		setData(newData);
		submit();
	};

	return (
		<Grid
			container
			width="100%"
			direction="column"
			spacing={2}
			padding={3}
			alignItems="center"
		>
			<Grid item>
				<Typography variant="h5" textAlign="center" fontWeight="bold">
					{t("RENTING.RENTING_DETAILS")}
				</Typography>
			</Grid>

			<Grid item>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<MobileDatePicker
						label={t("FIELDS.startDate")}
						inputFormat="DD/MM/YYYY"
						value={valueStartDate}
						onChange={handleChangeStartDate}
						renderInput={(params) => <TextField {...params} fullWidth />}
					/>
				</LocalizationProvider>
			</Grid>

			<Grid item>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<MobileDatePicker
						label={t("RENTING.END_DATE")}
						inputFormat="DD/MM/YYYY"
						value={valueDate}
						onChange={handleChangeEndDate}
						renderInput={(params) => <TextField {...params} fullWidth />}
					/>
				</LocalizationProvider>
			</Grid>

			<Grid item>
				<TextField
					label={t("FIELDS.pricePerDayEuro")}
					fullWidth
					value={valuePrice}
					onChange={(e) => setValuePrice(e.target.value)}
					type="number"
					variant="outlined"
				/>
			</Grid>

			<Grid item>
				<TextField
					label={t("FIELDS.responsible")}
					fullWidth
					value={valueResp}
					onChange={(e) => setValueResp(e.target.value)}
					variant="outlined"
				/>
			</Grid>

			<Grid item>
				<Typography variant="subtitle1" textAlign="center">
					{t("RENTING.ESTIMATED_VALUE", { value: valuePayment })}
				</Typography>
			</Grid>

			<Grid item>
				<Button
					variant="contained"
					color="primary"
					onClick={saveDetails}
					sx={{
						backgroundColor: "#63D77E",
						color: "white",
						"&:hover": { backgroundColor: "#4CAF50" },
					}}
				>
					{t("ACTIONS.SAVE")}
				</Button>
			</Grid>
		</Grid>
	);
};

export default function RentingPopup(props) {
	const { t } = useTranslation();
	const { state, setState, id, setActivate, onRented } = props;
	const [chosenClient, setChosenClient] = useState(null);
	const [data, setData] = useState(null);
	const [activeStep, setActiveStep] = useState(0);

	const [createRent, { error: errorCreate }] = useMutation(
		CreateRenting
	);

	const submitData = () => {
		if (chosenClient && data) {
			createRent({
				variables: {
					pricePerDay: parseInt(data.pricePerDay),
					client: chosenClient,
					machine: id,
					startedRent: data.startedRent,
					previsionEndingRent: data.previsionEndingRent,
				},
				onCompleted: () => {
					setActivate(true);
					onRented?.();
					setState(false);
				},
			});
		}
	};

	return (
		<Dialog
			open={state}
			onClose={() => setState(false)}
			fullWidth
			maxWidth="md"
			BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.9)" } }}
		>
			<Grid container direction="column" padding={4} spacing={2}>
				{activeStep === 0 && (
					<ChooseClient setChosenClient={setChosenClient} setActiveStep={setActiveStep} />
				)}
				{activeStep === 1 && (
					<MoreDetails setData={setData} dataHere={data} submit={submitData} />
				)}

				<Grid item>
					<Stepper activeStep={activeStep} sx={{ marginBottom: 2 }}>
						<Step onClick={() => setActiveStep(0)}>
							<StepLabel>{t("RENTING.CHOOSE_CLIENT")}</StepLabel>
						</Step>
						<Step onClick={() => setActiveStep(1)}>
							<StepLabel>{t("RENTING.RENTING_DETAILS")}</StepLabel>
						</Step>
					</Stepper>

					{errorCreate && (
						<Alert severity="error">{getErrorMessage(errorCreate)}</Alert>
					)}
				</Grid>
			</Grid>
		</Dialog>
	);
}
